import * as ActionTypes from "../actions/types";
import {
  parseDefinitions,
  parseTryout,
  TRYOUT_STATE,
  TRYOUT_STATE_FORM,
} from "../components/redoc-tryout/helpers/tryout";
import { getTryoutSettings } from "../utils/tryoutStorage";
import v4 from "uuid";

const INITIAL_STATE = {
  byId: {}, //here the path is the ID
  allIds: [],
  valueHistory: {}, //name - value object with form inputs
  authCode: {
    code: null,
    error: null,
  },
  accessToken: {
    access_token: null,
    expires_in: null,
    token_type: null,
    error: null,
    loading: false,
  },
};

// Helper function to safely get nested value
const getNestedValue = (obj, path) => {
  return path.reduce((current, key) => {
    return current && current[key];
  }, obj);
};

// Helper function to safely set nested value
const setNestedValue = (obj, path, value) => {
  if (path.length === 0) return;

  let current = obj;
  for (let i = 0; i < path.length - 1; i++) {
    if (!current[path[i]]) {
      current[path[i]] = {};
    }
    current = current[path[i]];
  }
  current[path[path.length - 1]] = value;
};

// Helper function to delete nested value
const deleteNestedValue = (obj, path) => {
  if (path.length === 0) return;

  let current = obj;
  for (let i = 0; i < path.length - 1; i++) {
    if (!current[path[i]]) return;
    current = current[path[i]];
  }
  delete current[path[path.length - 1]];
};

function createTryout(state, action) {
  const { documentationId, path, method, security, parameters, form } =
    action.payload;

  console.log('[TRYOUT DEBUG] Reducer: createTryout called:', {
    documentationId,
    path,
    method,
    hasExistingState: !!state.byId[documentationId],
  });

  const existingTryout = getNestedValue(state, [
    "byId",
    documentationId,
    path,
    method,
  ]);
  const tryout = parseTryout(existingTryout);
  
  // Get saved settings for orientation
  const savedSettings = getTryoutSettings();

  const newTryout =
    tryout && tryout.path && tryout.method
      ? {
          ...tryout,
          security,
          documentationId,
          parameters,
          form,
          loading: false,
          // Preserve existing horLayout or use saved setting
          horLayout: tryout.horLayout !== undefined ? tryout.horLayout : savedSettings.horLayout,
        }
      : {
          ...TRYOUT_STATE,
          path,
          method,
          documentationId,
          security,
          parameters,
          form,
          // Use saved horLayout for new tryouts
          horLayout: savedSettings.horLayout,
        };

  // Ensure nested structure exists
  if (!state.byId[documentationId]) {
    state.byId[documentationId] = {};
  }
  if (!state.byId[documentationId][path]) {
    state.byId[documentationId][path] = {};
  }

  state.byId[documentationId][path][method] = newTryout;
  
  console.log('[TRYOUT DEBUG] Reducer: createTryout completed, tryout created:', {
    path,
    method,
    hasParameters: !!newTryout.parameters,
    hasSecurity: !!newTryout.security,
  });
}

function updateTryout(state, action) {
  const { documentationId, path, method, values, timestamp } = action.payload;

  console.log('[TRYOUT DEBUG] Reducer: updateTryout called:', {
    documentationId,
    path,
    method,
    updatingValues: Object.keys(values || {}),
    timestamp,
  });

  const existingTryout = getNestedValue(state, [
    "byId",
    documentationId,
    path,
    method,
  ]);
  const tryout = parseTryout(existingTryout);

  const updatedTryout = {
    ...tryout,
    ...values,
    timestamp, // Include timestamp in the updated tryout
  };

  // Ensure nested structure exists
  if (!state.byId[documentationId]) {
    state.byId[documentationId] = {};
  }
  if (!state.byId[documentationId][path]) {
    state.byId[documentationId][path] = {};
  }

  state.byId[documentationId][path][method] = updatedTryout;
  
  console.log('[TRYOUT DEBUG] Reducer: updateTryout completed:', {
    formShown: updatedTryout.formShown,
    minified: updatedTryout.minified,
    timestamp: updatedTryout.timestamp,
  });
}

function showTryout(state, action) {
  const { documentationId, path, method } = action.payload;

  console.log('[TRYOUT DEBUG] Reducer: showTryout called:', {
    documentationId,
    path,
    method,
  });

  const existingTryout = getNestedValue(state, [
    "byId",
    documentationId,
    path,
    method,
  ]);
  
  console.log('[TRYOUT DEBUG] Reducer: showTryout existing tryout:', {
    exists: !!existingTryout,
    hasForm: !!existingTryout?.form,
    hasParameters: !!existingTryout?.parameters,
  });
  
  const { parameters, security } = parseDefinitions(existingTryout);

  let form = existingTryout?.form || { ...TRYOUT_STATE_FORM };

  // Process parameters and security
  [
    ...(parameters ? parameters : []),
    ...(security ? Object.keys(security).map((key) => security[key]) : []),
  ].forEach((param) => {
    const historyValues = state.valueHistory[param.name];
    if (historyValues && Array.isArray(historyValues)) {
      const newValue =
        form[param.in] && form[param.in].hasOwnProperty(param.name)
          ? form[param.in][param.name]
          : undefined;
      const historyValue = historyValues[historyValues.length - 1];

      if (!form[param.in]) {
        form[param.in] = {};
      }
      form[param.in][param.name] =
        newValue === undefined ? historyValue : newValue;
    }
  });

  // Ensure nested structure exists
  if (!state.byId[documentationId]) {
    state.byId[documentationId] = {};
  }
  if (!state.byId[documentationId][path]) {
    state.byId[documentationId][path] = {};
  }
  if (!state.byId[documentationId][path][method]) {
    state.byId[documentationId][path][method] = {};
  }

  // Remove response if it exists
  if (state.byId[documentationId][path][method].response) {
    delete state.byId[documentationId][path][method].response;
  }

  // Update the tryout
  state.byId[documentationId][path][method] = {
    ...state.byId[documentationId][path][method],
    form,
    formShown: true,
    toggleForm: true,
  };
  
  console.log('[TRYOUT DEBUG] Reducer: showTryout completed:', {
    formShown: true,
    toggleForm: true,
    stateUpdated: true,
  });
}

function sendRequest(state, action) {
  const { documentationId, path, method, values } = action.payload;

  const existingTryout = getNestedValue(state, [
    "byId",
    documentationId,
    path,
    method,
  ]);
  const tryout = parseTryout(existingTryout);

  const { args } = values;

  const item = {
    ...tryout,
    ...args,
    favorite: false,
    response: values.response,
    created: new Date().toString(),
    uuid: v4(),
  };

  // Update value history
  if (tryout.form) {
    Object.keys(tryout.form).forEach((paramIn) => {
      if (paramIn === "body") return;
      if (tryout.form[paramIn]) {
        Object.keys(tryout.form[paramIn]).forEach((name) => {
          const value = tryout.form[paramIn][name];
          if (!state.valueHistory[name]) {
            state.valueHistory[name] = [];
          }
          if (value && !state.valueHistory[name].includes(value)) {
            state.valueHistory[name].push(value);
          }
        });
      }
    });
  }

  // Ensure nested structure exists
  if (!state.byId[documentationId]) {
    state.byId[documentationId] = {};
  }

  // Set response
  state.byId[documentationId].response = {
    ...values.response,
    created: item.created,
  };

  // Add to request history
  if (!state.byId[documentationId].requestHistory) {
    state.byId[documentationId].requestHistory = {};
  }
  state.byId[documentationId].requestHistory[item.uuid] = item;

  // Update tryout
  if (!state.byId[documentationId][path]) {
    state.byId[documentationId][path] = {};
  }
  state.byId[documentationId][path][method] = {
    ...tryout,
    ...values,
  };
}

function favoriteTryoutHistoryItem(state, action) {
  const { documentationId, uuid, value } = action.payload;

  if (state.byId[documentationId]?.requestHistory?.[uuid]) {
    state.byId[documentationId].requestHistory[uuid].favorite = value;
  }
}

function editTryoutDescription(state, action) {
  const { documentationId, uuid, value, path, method } = action.payload;

  if (uuid) {
    if (state.byId[documentationId]?.requestHistory?.[uuid]) {
      state.byId[documentationId].requestHistory[uuid].description = value;
    }
  } else {
    if (state.byId[documentationId]?.[path]?.[method]) {
      state.byId[documentationId][path][method].description = value;
    }
  }
}

function deleteTryoutHistoryItem(state, action) {
  const { uuid, documentationId } = action.payload;

  if (state.byId[documentationId]?.requestHistory?.[uuid]) {
    delete state.byId[documentationId].requestHistory[uuid];
  }
}

function tryoutResponseClear(state, action) {
  const { documentationId } = action.payload;

  if (state.byId[documentationId]?.response) {
    delete state.byId[documentationId].response;
  }
}

function tryoutSetAuthCode(state, action) {
  const { code, error } = action.payload;
  state.authCode = {
    code,
    error,
  };
}

function tryoutClearAuthCode(state) {
  state.authCode = {
    code: null,
    error: null,
  };
}

function tryoutGetAccessToken(state) {
  state.accessToken = { loading: true };
}

function tryoutSetAccessToken(state, action) {
  const { access_token, expires_in, token_type } = action.payload;
  state.accessToken = {
    access_token,
    expires_in,
    token_type,
    error: null,
    loading: false,
  };
}

function tryoutClearAccessToken(state) {
  state.accessToken = {
    access_token: null,
    expires_in: null,
    token_type: null,
    error: null,
    loading: false,
  };
}

function tryoutAccessTokenFailed(state) {
  state.accessToken = {
    access_token: null,
    expires_in: null,
    token_type: null,
    error: "Something went wrong.",
    loading: false,
  };
}

function importTryoutHistory(state, action) {
  const { payload } = action;

  payload.forEach((payloadItem) => {
    const item = {
      ...payloadItem,
      imported: true,
      loading: false,
      created: new Date().toString(),
    };
    const { documentationId } = item;

    if (documentationId) {
      // Ensure nested structure exists
      if (!state.byId[documentationId]) {
        state.byId[documentationId] = {};
      }
      if (!state.byId[documentationId].requestHistory) {
        state.byId[documentationId].requestHistory = {};
      }

      // Only add if not already present
      if (!state.byId[documentationId].requestHistory[item.uuid]) {
        state.byId[documentationId].requestHistory[item.uuid] = item;
      }
    }
  });
}

function clearTryoutHistory(state, action) {
  const { documentationId } = action.payload;

  if (state.byId[documentationId]?.requestHistory) {
    state.byId[documentationId].requestHistory = {};
  }
}

export const tryout = {
  initialState: INITIAL_STATE,
  handlers: {
    [ActionTypes.CREATE_TRYOUT]: createTryout,
    [ActionTypes.UPDATE_TRYOUT]: updateTryout,
    [ActionTypes.SHOW_TRYOUT]: showTryout,
    [ActionTypes.TRYOUT_REQUEST_COMPLETED]: sendRequest,
    [ActionTypes.FAVORITE_TRYOUT_HISTORY_ITEM]: favoriteTryoutHistoryItem,
    [ActionTypes.DELETE_TRYOUT_HISTORY_ITEM]: deleteTryoutHistoryItem,
    [ActionTypes.TRYOUT_RESPONSE_CLEAR]: tryoutResponseClear,
    [ActionTypes.IMPORT_TRYOUT_HISTORY]: importTryoutHistory,
    [ActionTypes.TRYOUT_SET_AUTH_CODE]: tryoutSetAuthCode,
    [ActionTypes.TRYOUT_CLEAR_AUTH_CODE]: tryoutClearAuthCode,
    [ActionTypes.EDIT_TRYOUT_DESCRIPTION]: editTryoutDescription,
    [ActionTypes.TRYOUT_GET_ACCESS_TOKEN]: tryoutGetAccessToken,
    [ActionTypes.STORE_TRYOUT_ACCESS_TOKEN]: tryoutSetAccessToken,
    [ActionTypes.TRYOUT_CLEAR_ACCESS_TOKEN]: tryoutClearAccessToken,
    [ActionTypes.STORE_TRYOUT_ACCESS_TOKEN_FAILED]: tryoutAccessTokenFailed,
    [ActionTypes.CLEAR_TRYOUT_HISTORY]: clearTryoutHistory,
  },
};
